import { ETypePersonneJuridique } from "common/enums/ClientSheet/ETypePersonneJuridique";
import InvestorInvitationCreateRequestResource from "common/resources/InvestorInvitation/InvestorInvitationCreateRequestResource";
import InvestorInvitationLinkResponseResource from "common/resources/InvestorInvitation/InvestorInvitationLinkResponseResource";
import InvestorInvitationProductResponseResource from "common/resources/InvestorInvitation/InvestorInvitationProductResponseResource";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonVariant } from "components/elements/Button";
import MessageBox from "components/elements/MessageBox";
import Modal from "components/elements/Modal";
import Typography, { ITypo, ITypoColor, ITypoComponent } from "components/elements/Typography";
import Form from "components/materials/Form";
import InputElement, { EInputType } from "components/materials/Form/InputElement";
import RadioInputElement from "components/materials/Form/RadioInputElement";
import I18n from "components/materials/I18n";
import { FormEvent, useCallback, useMemo, useState } from "react";
import InvestorInvitationService from "services/InvestorInvitationService";
import { container } from "tsyringe";

import classes from "./classes.module.scss";

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	/** Produits du cabinet ouverts au portail. `null` tant qu'ils chargent. */
	products: InvestorInvitationProductResponseResource[] | null;
	onCreated: (response: InvestorInvitationLinkResponseResource) => void;
};

const investorInvitationService = container.resolve(InvestorInvitationService);

export default function InviteClientModal({ isOpen, onClose, products, onCreated }: IProps) {
	const copy = I18n.asset.pages.investor_invitations.invite_modal;

	return (
		<Modal isOpen={isOpen} onClose={onClose} className={classes["modal"]}>
			<div className={classes["root"]}>
				<div className={classes["heading"]}>
					<Typography typo={ITypo.H1} component={ITypoComponent.H2}>
						{copy.title}
					</Typography>
					<Typography typo={ITypo.P_MEDIUM} color={ITypoColor.WILD_SAND_700}>
						{copy.description}
					</Typography>
				</div>

				{products === null && <Typography typo={ITypo.P_MEDIUM}>{copy.loading_products}</Typography>}

				{products?.length === 0 && (
					<>
						<MessageBox type="info" text={copy.no_product} />
						<Button variant={EButtonVariant.OUTLINED} onClick={onClose} className={classes["close"]}>
							{I18n.asset.common.close}
						</Button>
					</>
				)}

				{/* La fenêtre démonte son contenu à la fermeture : le formulaire repart de zéro à chaque ouverture. */}
				{products && products.length > 0 && <InviteClientForm products={products} onClose={onClose} onCreated={onCreated} />}
			</div>
		</Modal>
	);
}

type IFormProps = {
	products: InvestorInvitationProductResponseResource[];
	onClose: () => void;
	onCreated: (response: InvestorInvitationLinkResponseResource) => void;
};

function InviteClientForm({ products, onClose, onCreated }: IFormProps) {
	const copy = I18n.asset.pages.investor_invitations.invite_modal;
	const [errors, setErrors] = useState<ValidationError[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [personType, setPersonType] = useState<ETypePersonneJuridique>(ETypePersonneJuridique.personne_physique);
	// Un seul produit éligible : il est présélectionné.
	const [productSheetId, setProductSheetId] = useState<string | null>(products.length === 1 ? products[0]?.productSheetId ?? null : null);

	const isCompany = personType === ETypePersonneJuridique.personne_morale;

	const productOptions = useMemo(
		() =>
			products.map((candidate) => ({
				value: candidate.productSheetId,
				label: candidate.name,
			})),
		[products],
	);

	const submit = useCallback(
		async (_event: FormEvent<HTMLFormElement>, formData: { [key: string]: unknown }) => {
			const text = (name: string) => String(formData[name] ?? "").trim();
			const resource = InvestorInvitationCreateRequestResource.hydrate<InvestorInvitationCreateRequestResource>({
				personType,
				firstName: text("firstName"),
				lastName: text("lastName"),
				email: text("email").toLowerCase(),
				mobilePhone: text("mobilePhone") || null,
				companyName: isCompany ? text("companyName") || null : null,
				productSheetId: productSheetId ?? "",
			});

			setErrors([]);
			setIsSubmitting(true);
			try {
				await resource.validateOrReject();
				onCreated(await investorInvitationService.create(resource));
			} catch (error) {
				if (Array.isArray(error)) setErrors(error as ValidationError[]);
			} finally {
				setIsSubmitting(false);
			}
		},
		[isCompany, onCreated, personType, productSheetId],
	);

	return (
		<Form className={classes["form"]} errors={errors} onSubmit={submit}>
			<RadioInputElement
				name="personType"
				label={copy.person_type}
				defaultValue={personType}
				options={Object.values(ETypePersonneJuridique).map((type) => ({ value: type, label: I18n.asset.enums.ETypePersonneJuridique[type] }))}
				onChange={(event) => setPersonType(event.target.value as ETypePersonneJuridique)}
			/>

			{isCompany && <InputElement name="companyName" type={EInputType.TEXT} label={copy.company_name} caption={copy.company_name_caption} />}

			<div className={classes["identity"]}>
				<div className={classes["names"]}>
					<InputElement name="firstName" type={EInputType.TEXT} label={copy.first_name} autoComplete="off" />
					<InputElement name="lastName" type={EInputType.TEXT} label={copy.last_name} autoComplete="off" />
				</div>
				{isCompany && (
					<Typography typo={ITypo.CAPTION} color={ITypoColor.WILD_SAND_900}>
						{copy.contact_caption}
					</Typography>
				)}
			</div>

			<InputElement name="email" type={EInputType.TEXT} label={copy.email} caption={copy.email_caption} autoComplete="off" />
			<InputElement name="mobilePhone" type={EInputType.TEXT} label={copy.mobile} caption={copy.mobile_caption} placeholder="06 12 34 56 78" autoComplete="off" />

			<RadioInputElement
				name="productSheetId"
				label={copy.product}
				defaultValue={productSheetId ?? undefined}
				options={productOptions}
				onChange={(event) => setProductSheetId(event.target.value)}
			/>

			<div className={classes["buttons"]}>
				<Button variant={EButtonVariant.OUTLINED} onClick={onClose} disabled={isSubmitting}>
					{I18n.asset.common.cancel}
				</Button>
				<Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
					{copy.submit}
				</Button>
			</div>
		</Form>
	);
}
